import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import './Dialog.css';

// Promise-based confirm / prompt / alert. Drop-in replacement for window.* —
// callsites become `await dialog.confirm("…")` instead of `window.confirm("…")`.
// One provider mounted at AdminLayout root; one stack so dialogs queue cleanly.

const DialogCtx = createContext(null);
export const useDialog = () => useContext(DialogCtx);

let _idSeq = 1;

export function DialogProvider({ children }) {
  const [stack, setStack] = useState([]);

  const open = useCallback((spec) => new Promise((resolve) => {
    const id = _idSeq++;
    setStack(s => [...s, { id, ...spec, resolve }]);
  }), []);

  const close = useCallback((id, value) => {
    setStack(s => {
      const item = s.find(x => x.id === id);
      if (item) item.resolve(value);
      return s.filter(x => x.id !== id);
    });
  }, []);

  const api = {
    alert:   (message, opts = {})         => open({ kind: 'alert',   message, ...opts }),
    confirm: (message, opts = {})         => open({ kind: 'confirm', message, ...opts }),
    prompt:  (message, defaultValue = '', opts = {}) => open({ kind: 'prompt',  message, defaultValue, ...opts }),
  };

  return (
    <DialogCtx.Provider value={api}>
      {children}
      {stack.map(d => <DialogShell key={d.id} d={d} onClose={(v) => close(d.id, v)} />)}
    </DialogCtx.Provider>
  );
}

function DialogShell({ d, onClose }) {
  const [value, setValue] = useState(d.defaultValue || '');
  const inputRef = useRef(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    setTimeout(() => (inputRef.current || cancelRef.current)?.focus(), 30);
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose(d.kind === 'alert' ? undefined : (d.kind === 'confirm' ? false : null));
      } else if (e.key === 'Enter' && d.kind !== 'prompt') {
        e.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = () => {
    if (d.kind === 'prompt') onClose(value);
    else if (d.kind === 'confirm') onClose(true);
    else onClose(undefined);
  };

  const cancel = () => onClose(d.kind === 'confirm' ? false : null);

  const danger = !!d.danger;
  const okLabel = d.okLabel || (d.kind === 'confirm' ? 'Confirm' : 'OK');
  const cancelLabel = d.cancelLabel || 'Cancel';

  return (
    <div className="dlg__backdrop" onMouseDown={cancel}>
      <div role="dialog" aria-modal="true" aria-label={d.title || 'Dialog'}
           className={`dlg ${danger ? 'dlg--danger' : ''}`}
           onMouseDown={e => e.stopPropagation()}>
        {d.title && <div className="dlg__title">{d.title}</div>}
        <div className="dlg__msg">{d.message}</div>
        {d.kind === 'prompt' && (
          <input
            ref={inputRef}
            className="dlg__input"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
            placeholder={d.placeholder || ''}
            type={d.inputType || 'text'}
          />
        )}
        <div className="dlg__row">
          {d.kind !== 'alert' && (
            <button ref={cancelRef} type="button" className="dlg__btn dlg__btn--ghost" onClick={cancel}>
              {cancelLabel}
            </button>
          )}
          <button type="button"
                  className={`dlg__btn ${danger ? 'dlg__btn--danger' : 'dlg__btn--primary'}`}
                  onClick={submit}>
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
